/* eslint-disable no-shadow */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Menu, message } from 'antd';
import {
  CloudDownloadOutlined,
  EditOutlined,
  TagOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  ClipboardText,
  FolderSimplePlus,
  ListChecks,
} from '@phosphor-icons/react';
import { BCP_NUMBERS } from '../../../global/bcpNumbers';

import RecordsHOC from '../RecordsHOC';
import { Header } from '../../../components';
import { assignedColumns } from '../shared/constants';

import { openModal } from '../../../actions/modal';
import {
  getAssignedRecordsByPage,
  getAssignedByQuery,
  printUserRecord,
  downloadZip,
  triggerAutoUpload,
  markDocumentAsCompleted,
  assignBackAndComplete,
} from '../../../actions/records';

import download from '../../../actions/helpers/download';
import { makeSingleFlight } from '../../../utils/singleFlight';
import { verifyFeatureAccess, chartClarityTooltips } from '../../../global/featureFlags';
import ChartActionsMenu from '../../../components/chartActions/ChartActionsMenu';
import {
  handleSendToChart,
  handleCreateChart,
  handleCreateTask,
  buildCreateTaskModalData,
  handleDirectAutoUpload,
  normalizeRecord,
} from '../../../components/chartActions/helpers';
import {
  canUseStandaloneTask,
  fetchStandaloneTaskSupport,
} from '../../../components/chartActions/credentialCapabilities';
import {
  ASSIGN_BACK_ERROR_MESSAGE,
  assignBackThenComplete,
} from '../shared/assignBackCompletion';

/* global toastr */

function parseAssignee(queryString) {
  const regex = /(?:\?|&)assignee=([^&]+)/;
  const match = regex.exec(queryString);
  return match ? decodeURIComponent(match[1]) : null;
}

export class AssignedRecords extends Component {
  constructor(props) {
    super(props);
    this.state = {
      autoUploadToEMR: false,
      createPatientChart: false,
      hideTaskButton: false,
      taskCreation: false,
      credentialSupportsStandaloneTask: !props.hasEmrIntegrationCredential,
      markCompleteButtonOption: localStorage.mark_complete_reassign_option || 'Mark Complete',
    };

    // Single-flight guards so a fast double-click can't fire two completion/assign requests.
    this.markComplete = makeSingleFlight((...args) => this.runMarkComplete(...args));
    this.markCompleteAndReassign = makeSingleFlight((...args) => this.runMarkCompleteAndReassign(...args));
    this.markCompleteAndAssignBack = makeSingleFlight((...args) => this.runMarkCompleteAndAssignBack(...args));
  }

  componentDidMount = async () => {
    const autoUploadToEMR = await verifyFeatureAccess('manual_upload_emr');
    const createPatientChart = await verifyFeatureAccess('create_patient_chart');
    const hideTaskButton = await verifyFeatureAccess('hide_task_creation_button');
    const taskCreation = await verifyFeatureAccess('task_creation');
    const credentialSupportsStandaloneTask = this.props.hasEmrIntegrationCredential
      ? await fetchStandaloneTaskSupport()
      : true;
    this.setState({
      autoUploadToEMR,
      createPatientChart,
      hideTaskButton,
      taskCreation,
      credentialSupportsStandaloneTask,
    });
  };

  downloadRecord = async () => {
    const {
      selectedData,
      data: allDataOnPage,
      openModal,
      downloadZip,
      printUserRecord,
    } = this.props;
    const oneRecordSelected = selectedData.length === 1;
    const hasOneReceivedRecord = allDataOnPage.length === 1;
    const selectedAll = selectedData[0] === 'all';

    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      // Download one individual record
      const file = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
      const {
        file_token: recordId,
        record_status: recordStatus,
      } = file;

      // Users shouldn't be able to download revoked records
      if (recordStatus === 'revoked')
        return toastr.error(
          'You can not download a record that has been revoked'
        );

      // Get the signed url for the record we want to download
      const downloadData = await printUserRecord(recordId);
      const resolvedDownloadUrl = downloadData?.signedUrl || this.props.downloadUrl;
      const resolvedDownloadFilename = downloadData?.downloadFilename || this.props.downloadFilename || 'file';

      if (!resolvedDownloadUrl) {
        return toastr.error(
          'Unable to download this record. Try refreshing and downloading again.'
        );
      }

      await download(resolvedDownloadUrl, resolvedDownloadFilename);
      return;
    }

    // Open modal to show download
    openModal({ type: 'DOWNLOAD_ZIP' });

    // If the select all is checked, we will use the data for the entire page as it is stored
    // in redux. Otherwise, we'll take the multiple selected rows
    // This returns an array of file tokens to be downloaded in a single zip file
    const recordsToDownload = (selectedAll ? allDataOnPage : selectedData)
      // Don't allow users to download records that have been revoked
      // TODO (Ammar Khan) We need to enforce this on the backend
      .filter((record) => record.record_status !== 'revoked')
      .map((x) => x.file_token);
    await downloadZip(recordsToDownload);
  };

  editTag = async () => {
    const { selectedData, data: allDataOnPage, openModal } = this.props;
    const oneRecordSelected = selectedData.length === 1;
    const hasOneReceivedRecord = allDataOnPage.length === 1;
    const selectedAll = selectedData[0] === 'all';

    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      // Add tag to one receiver.
      const receiver = hasOneReceivedRecord
        ? allDataOnPage[0]
        : selectedData[0];
      const {
        send_token: sendToken,
        tag,
        receiver_type: receiverType,
      } = receiver;

      const modalType = {
        type: 'ADD_LABEL_TO_RECORD',
        data: {
          reportAiDetails: {
            isLabelEdited: selectedData[0].is_label_edited,
            hasWrongLabelingBeenReported:
              selectedData[0].has_wrong_labeling_been_reported,
            aiStatus: selectedData[0].ai_status,
          },
          record: sendToken,
          tag,
          receiverType,
        },
      };
      openModal(modalType);
      return;
    }

    if (selectedData.length > 1) {
      const sendTokens = [];
      const receiverTypes = [];
      for (let i = 0; i < selectedData.length; i += 1) {
        sendTokens.push(selectedData[i].send_token);
        receiverTypes.push(selectedData[i].receiver_type);
      }

      const modalType = {
        type: 'ADD_LABEL_TO_MULTIPLE_RECORDS',
        data: {
          record: sendTokens,
          receiverTypes,
        },
      };
      openModal(modalType);
      return;
    }

    // If the select all is checked, we will use the data for the entire page as it is stored
    // in redux. Otherwise, we'll take the multiple selected rows
    // This returns an array of send tokens to tag.
    const sendTokens = (selectedAll ? allDataOnPage : selectedData).map(
      (x) => x.send_token
    );

    const modalType = {
      type: 'ADD_LABEL_TO_RECORD',
      data: {
        record: sendTokens,
      },
    };
    openModal(modalType);
  };

  newChart = async () => {
    const { selectedData, data: allDataOnPage, openModal } = this.props;
    const oneRecordSelected = selectedData.length === 1;
    const hasOneReceivedRecord = allDataOnPage.length === 1;
    const selectedAll = selectedData[0] === 'all';
    //  left this logic, though I don't think it's needed

    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      // Add tag to one receiver.
      const receiver = hasOneReceivedRecord
        ? allDataOnPage[0]
        : selectedData[0];
      const {
        patient_dob: PatientDOB,
        patient_name: PatientName,
        send_token: sendToken,
        receiver_type: receiverType,
        api_credentials: apiCredentials,
      } = receiver;

      const modalType = {
        type: 'CREATE_NEW_CHART',
        data: {
          name: PatientName,
          patient_first_name: selectedData[0].patient_first_name,
          patient_middle_name: selectedData[0].patient_middle_name,
          patient_last_name: selectedData[0].patient_last_name,
          dob: PatientDOB,
          record: sendToken,
          gender: selectedData[0].patient_gender,
          phoneNumber: selectedData[0].patient_phone_number,
          patient_email: selectedData[0].patient_email,
          address: selectedData[0].patient_address,
          zipCode: selectedData[0].patient_zip_code,
          city: selectedData[0].patient_city,
          state: selectedData[0].patient_state,
          practitionerName: selectedData[0].practitioner_name,
          referring_provider: selectedData[0].referring_provider,
          provider_office: selectedData[0].provider_office,
          primary_plan: selectedData[0].primary_plan,
          primaryMemberId: selectedData[0].primary_member_id,
          payerName: selectedData[0].payer_name,
          groupNumber: selectedData[0].group_number,
          primaryPlanName: selectedData[0].primary_plan_name,
          secondaryPlanName: selectedData[0].secondary_plan_name,
          icdCodes: this.props.selectedData[0].icd_codes,
          primaryInsurancePoBox: this.props.selectedData[0].primary_insurance_po_box,
          primaryInsuranceState: this.props.selectedData[0].primary_insurance_state,
          secondaryInsurancePoBox: this.props.selectedData[0].secondary_insurance_po_box,
          secondaryInsuranceState: this.props.selectedData[0].secondary_insurance_state,
          marketingRep: this.props.selectedData[0].marketing_rep,
          secondaryPayerName: this.props.selectedData[0].secondary_payer_name,
          secondaryMemberId: this.props.selectedData[0].secondary_member_id,
          secondaryGroupNumber: this.props.selectedData[0].secondary_group_number,
          receiverType,
          apiCredentials,
          receivedAt: selectedData[0].received_at,
          callerName: selectedData[0].caller_name,
          documentCategory: this.props.selectedData[0].document_category,
          authorizationNumber: this.props.selectedData[0].authorization_number,
          authorizationFromDate: this.props.selectedData[0].authorization_from_date,
          authorizationToDate: this.props.selectedData[0].authorization_to_date,
          authorizationNumberOfVisits: this.props.selectedData[0].authorization_number_of_visits,
          authorizationDescription: this.props.selectedData[0].authorization_description,
        },
      };
      openModal(modalType);
      return;
    }

    // If the select all is checked, we will use the data for the entire page as it is stored
    // in redux. Otherwise, we'll take the multiple selected rows
    // This returns an array of send tokens to tag.
    const sendTokens = (selectedAll ? allDataOnPage : selectedData).map(
      (x) => x.send_token
    );

    const modalType = {
      type: 'ADD_LABEL_TO_RECORD',
      data: {
        record: sendTokens,
      },
    };
    openModal(modalType);
  };

  createTask = async () => {
    const { selectedData, data: allDataOnPage, openModal } = this.props;
    const oneRecordSelected = selectedData.length === 1;
    const hasOneReceivedRecord = allDataOnPage.length === 1;
    const selectedAll = selectedData[0] === 'all';
    //  left this logic, though I don't think it's needed

    if ((oneRecordSelected && !selectedAll) || hasOneReceivedRecord) {
      // Add tag to one receiver.
      const receiver = hasOneReceivedRecord
        ? allDataOnPage[0]
        : selectedData[0];
      const {
        send_token: sendToken,
        receiver_type: receiverType,
      } = receiver;
      const record = selectedAll ? receiver : (selectedData[0] || receiver);

      const modalType = {
        type: 'CREATE_TASK',
        data: buildCreateTaskModalData(record, {
          receiver,
          sendToken,
          receiverType,
        }),
      };
      openModal(modalType);
    }
  };

  editPatientOpen = () => {
    const { selectedData, data: allDataOnPage, openModal } = this.props;
    const hasOneReceivedRecord = allDataOnPage.length === 1;
    const receiver = hasOneReceivedRecord ? allDataOnPage[0] : selectedData[0];
    const {
      send_token: sendToken,
      tag,
      receiver_type: receiverType,
    } = receiver;
    const modalType = {
      type: 'EDIT_PATIENT',
      data: {
        patient: selectedData[0],
        fromPage: 'assigned',
        record: sendToken,
        tag,
        receiverType,
      },
    };
    openModal(modalType);
  };

  updateDocumentOpen = () => {
    const { selectedData, openModal } = this.props;

    const modalType = {
      type: 'UPDATE_ASSIGNED_DOCUMENT',
      data: {
        selectedDocumentStatus: selectedData[0].document_status_id,
        assignedDocumentId: selectedData[0].slug,
        assignee: {
          value: selectedData[0].assignee_email,
          label: selectedData[0].assignee,
          key: selectedData[0].assignee_email,
        },
      },
    };
    openModal(modalType);
  };

  runMarkComplete = async () => {
    localStorage.setItem('mark_complete_reassign_option', 'Mark Complete');
    this.setState({ markCompleteButtonOption: 'Mark Complete' });

    const { markDocumentAsCompleted, selectedData } = this.props;
    await markDocumentAsCompleted(selectedData[0].slug);

    const { error } = this.props;
    if (!error) {
      message.success({
        content: 'Your record has been marked as completed.',
      });

      window.location.reload();
    } else {
      message.error({
        content: 'Unable to mark as completed. Please try again.',
      });
    }
  };

  runMarkCompleteAndReassign = async () => {
    const { openModal, selectedData } = this.props;

    localStorage.setItem(
      'mark_complete_reassign_option',
      'Mark Complete and Reassign'
    );
    this.setState({ markCompleteButtonOption: 'Mark Complete and Reassign' });

    const modalType = {
      type: 'ASSIGN_RECORD',
      data: {
        record: selectedData[0].send_token,
        receiverType: selectedData[0].receiver_type,
        markComplete: true,
        assignedDocumentId: selectedData[0].slug,
      },
    };
    openModal(modalType);
  };

  runMarkCompleteAndAssignBack = async () => {
    const { selectedData } = this.props;

    localStorage.setItem(
      'mark_complete_reassign_option',
      'Mark Complete and Assign Back'
    );
    this.setState({
      markCompleteButtonOption: 'Mark Complete and Assign Back',
    });

    const { assignBackAndComplete } = this.props;
    const result = await assignBackThenComplete({
      assignedDocumentId: selectedData[0].slug,
      assignBackAndComplete,
    });

    if (result.ok) {
      message.success({
        content: 'The document has been assigned back to the assigner.',
      });

      // After 3 seconds, go to assigned records page.
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } else {
      message.error({
        content: ASSIGN_BACK_ERROR_MESSAGE,
      });
    }
  };

  // Checks if a row containing a reply is selected. It will loop through all the selected rows and
  // check if one of the rows is a reply to a sent record. If it is, it returns true that the
  // selectedData contains a reply. Otherwise return false.
  isAReplySelected = (selectedData) => {
    for (let i = 0; i < selectedData.length; i += 1) {
      if (selectedData[i].uri) {
        return true;
      }
    }
    return false;
  };

  // Checks if a row of type form is selected.
  isFormRecord = (selectedData) => selectedData.some((data) => data?.form_id);

  isEmailRecord = (selectedData) =>
    selectedData.some((data) => data?.attachment_id);

  buildSelectedRecord = (record) => {
    if (!record) {
      return null;
    }

    return normalizeRecord(record, {
      parentType: record.parent_type,
      dynamicSendToken: record.send_token,
      uploadReceiverType: record.receiver_type,
      isEmailAttachment: record.receiver_type === 'email_attachment',
      isEmailSubmission: record.is_email_submission === true,
      isSentContext: false,
    });
  };

  autoUploadRecord = async (selectedRecord) => {
    const {
      patient_name: patientName,
      patient_dob: patientDOB,
      send_token: sendToken,
      receiver_type: receiverType,
      api_credentials: apiCredentials,
    } = selectedRecord;
    const { autoUploadEmr, currentOrganizationId } = this.props;
    // If EMR is EMA, we will allow Send Chart Modal to open as Patient MRN is an option to send with
    // chart instead of Patient Name and DOB.
    if (autoUploadEmr !== 'EMA' && (!patientName || !patientDOB)) {
      message.error({
        content:
          'Cannot upload a document without a valid name and date of birth. Edit record details and try again.',
      });
    } else {
      const { openModal } = this.props;

      const fileNameSupported = [
        'Kareo',
        'OncoEMR',
        'Nymbl',
        'EMA',
        'AllegianceMD',
        'Elation',
      ];

      // We want to enable modal support for new ECW practices without affect the old practices' workflow
      if (
        fileNameSupported.includes(autoUploadEmr) ||
        (autoUploadEmr === 'ECW' && currentOrganizationId > 4206) || (autoUploadEmr === 'CareTracker' && currentOrganizationId === 4193)
      ) {
        let recordFileName = selectedRecord.document_title;
        if (!recordFileName && (selectedRecord.receiver_type === 'jotform_submission' || selectedRecord.is_email_submission === true)) {
          recordFileName = `${(selectedRecord.created_at || '').replace(/ /g, '_')}_${(selectedRecord.sender_name || '').replace(/ /g, '_')}`;
        } else if (!recordFileName) {
          recordFileName = `${(selectedRecord.received_at || '').replace(/ /g, '_')}_${(selectedRecord.caller_name || '').replace(/ /g, '_')}`;
        }
        const modalType = {
          type: 'AUTO_UPLOAD',
          data: {
            fileName: recordFileName,
            providerName: this.props.selectedData[0].practitioner_name,
            dateOfService: this.props.selectedData[0].date_of_service,
            patientName: this.props.selectedData[0].patient_name,
            patientFirstName: this.props.selectedData[0].patient_first_name,
            patientMiddleName: this.props.selectedData[0].patient_middle_name,
            patientLastName: this.props.selectedData[0].patient_last_name,
            patientDOB: this.props.selectedData[0].patient_dob,
            documentCategory: this.props.selectedData[0].document_category,
            sendToken,
            receiverType,
            autoUploadEmr,
            apiCredentials,
            currentOrganizationId,
          },
        };
        openModal(modalType);
      } else if (autoUploadEmr === 'AdvancedMD') {
        const modalType = {
          type: 'AUTO_UPLOAD',
          data: {
            providerName: this.props.selectedData[0].practitioner_name, // Once we have AI extraction, this will be replaced with the extracted value
            patientName: this.props.selectedData[0].patient_name,
            patientFirstName: this.props.selectedData[0].patient_first_name,
            patientMiddleName: this.props.selectedData[0].patient_middle_name,
            patientLastName: this.props.selectedData[0].patient_last_name,
            patientDOB: this.props.selectedData[0].patient_dob,
            documentCategory: this.props.selectedData[0].document_category,
            sendToken,
            receiverType,
            autoUploadEmr,
          },
        };
        openModal(modalType);
      } else if (autoUploadEmr === 'Brightree') {
        const modalType = {
          type: 'AUTO_UPLOAD',
          data: {
            patientName: this.props.selectedData[0].patient_name,
            patientFirstName: this.props.selectedData[0].patient_first_name,
            patientMiddleName: this.props.selectedData[0].patient_middle_name,
            patientLastName: this.props.selectedData[0].patient_last_name,
            patientDOB: this.props.selectedData[0].patient_dob,
            icdCodes: this.props.selectedData[0].icd_codes,
            primaryInsurancePoBox: this.props.selectedData[0].primary_insurance_po_box,
            primaryInsuranceState: this.props.selectedData[0].primary_insurance_state,
            secondaryInsurancePoBox: this.props.selectedData[0].secondary_insurance_po_box,
            secondaryInsuranceState: this.props.selectedData[0].secondary_insurance_state,
            marketingRep: this.props.selectedData[0].marketing_rep,
            sendToken,
            receiverType,
          },
        };
        openModal(modalType);
      } else {
        handleDirectAutoUpload({
          triggerAutoUpload: this.props.triggerAutoUpload,
          sendToken,
          receiverType,
        });
      }
    }
  };

  handleDropdownSendToChart = (selectedRecord) => {
    const record = this.buildSelectedRecord(selectedRecord);
    handleSendToChart({
      record,
      dependencies: {
        onOpenEhrDrawer: this.props.onOpenEhrDrawer,
      },
    });
  };

  handleDropdownCreateChart = (selectedRecord) => {
    const record = this.buildSelectedRecord(selectedRecord);
    handleCreateChart({
      record,
      dependencies: {
        onOpenEhrDrawer: this.props.onOpenEhrDrawer,
      },
    });
  };

  handleDropdownCreateTask = (selectedRecord) => {
    const record = this.buildSelectedRecord(selectedRecord);
    handleCreateTask({
      record,
      dependencies: {
        onOpenEhrDrawer: this.props.onOpenEhrDrawer,
      },
    });
  };

  isUploadDisabled(receiverType, receiver) {
    const isBCP = localStorage.m8_org === 'BCP Group';
    if (receiverType === 'received_fax') {
      // For BCP, they only want autoupload enabled for some of their fax numbers
      if (isBCP && !BCP_NUMBERS.includes(receiver)) {
        return true;
      }
      return false;
    }
    if (receiverType === 'email_attachment' && !isBCP) {
      return false;
    }
    if (receiverType === 'jotform_submission' && !isBCP) {
      return false;
    }
    if (receiverType === 'receiver' && !isBCP) {
      return false;
    }
    return true;
  }

  render() {
    const {
      selectedData,
      location: { search },
    } = this.props;
    const canEdit = !(
      selectedData.length === 1 &&
      (selectedData[0].can_edit_patient_info ||
        selectedData[0].can_edit_privacy_level)
    );
    const {
      autoUploadToEMR,
      hideTaskButton,
      createPatientChart,
      taskCreation,
      credentialSupportsStandaloneTask,
    } = this.state;
    const { departmentUsers } = this.props;
    let assignee = '';
    let assigneeName = 'me';
    if (search && search.toString().includes('assignee=')) {
      assignee = parseAssignee(search);
      departmentUsers.forEach((userObj) => {
        if (userObj[1] === assignee) {
          assigneeName = userObj[0].toString();
        }
      });
    }

    const pageTitle = `Assigned to ${assigneeName}`;
    const singleRecordSelected = selectedData.length === 1 && selectedData[0] !== 'all';
    const selectedRecord = singleRecordSelected ? selectedData[0] : null;
    const uploadDisabled = selectedRecord ? this.isUploadDisabled(selectedRecord.receiver_type, selectedRecord.receiver) : true;
    const dropdownDisabled = !selectedRecord || this.isAReplySelected(selectedData);
    const disableChartActions = uploadDisabled || dropdownDisabled;
    const clarityTooltips = chartClarityTooltips(this.props.autoUploadEmr);
    const canCreateTask = taskCreation && !hideTaskButton && canUseStandaloneTask({
      hasEmrIntegrationCredential: this.props.hasEmrIntegrationCredential,
      credentialSupportsStandaloneTask,
      autoUploadEmr: this.props.autoUploadEmr,
    });
    const hideChartDropdown = !(autoUploadToEMR || createPatientChart || canCreateTask);
    const markCompletedMenu = (
      <Menu>
        <Menu.Item key='0' onClick={() => this.markComplete()}>
          Mark Complete
        </Menu.Item>
        <Menu.Item key='1' onClick={() => this.markCompleteAndReassign()}>
          Mark Complete and Reassign
        </Menu.Item>
        <Menu.Item key='2' onClick={() => this.markCompleteAndAssignBack()}>
          Mark Complete and Assign Back
        </Menu.Item>
      </Menu>
    );

    return (
      <div>
        <Helmet>
          <title>Assigned Records - Medsender</title>
        </Helmet>
        <Header
          main={pageTitle}
          subtext='Choose a record below'
          locationPath={[
            { icon: 'home', text: 'Home', link: '/app' },
            { icon: 'inbox', text: 'Received', link: '/app/received' },
          ]}
          buttons={[
            {
              text: 'Download',
              icon: <CloudDownloadOutlined />,
              disabled:
                !selectedData.length >= 1 ||
                this.isAReplySelected(selectedData),
              func: () => this.downloadRecord(),
            },
            {
              type: 'dropdown',
              text: 'Chart Actions',
              icon: <ClipboardText size={18} weight="fill" />,
              disabled: dropdownDisabled,
              hide: !this.props.hasEmrIntegrationCredential || hideChartDropdown,
              menu: (
                <ChartActionsMenu
                  canSendToChart={autoUploadToEMR}
                  canCreateChart={createPatientChart}
                  canCreateTask={canCreateTask}
                  disableSendToChart={disableChartActions}
                  disableCreateChart={disableChartActions}
                  disableCreateTask={disableChartActions}
                  onSendToChart={() => selectedRecord && this.handleDropdownSendToChart(selectedRecord)}
                  onCreateChart={() => selectedRecord && this.handleDropdownCreateChart(selectedRecord)}
                  onCreateTask={() => selectedRecord && this.handleDropdownCreateTask(selectedRecord)}
                />
              ),
            },
            {
              text: 'Send to Chart',
              icon: <ClipboardText size={18} weight="fill" />,
              toolTiptitle: clarityTooltips.sendToChart,
              disabled:
                selectedData.length !== 1 ||
                this.isAReplySelected(selectedData) ||
                this.isUploadDisabled(selectedData[0].receiver_type, selectedData[0].receiver),
              hide: !autoUploadToEMR || this.props.hasEmrIntegrationCredential,
              func: () => this.autoUploadRecord(selectedData[0]),
            },
            {
              text: 'Create New Chart',
              icon: <FolderSimplePlus size={18} />,
              disabled:
                selectedData.length !== 1 ||
                this.isAReplySelected(selectedData) ||
                this.isUploadDisabled(selectedData[0].receiver_type, selectedData[0].receiver),
              hide: !createPatientChart || this.props.hasEmrIntegrationCredential,
              func: () => this.newChart(),
              //  not sure what isAReplySelected means exactly but added as it's used in others
            },
            {
              text: this.props.autoUploadEmr === 'Kareo' ? 'Create Note' : 'Create Task',
              icon: <ListChecks size={18} />,
              toolTiptitle: clarityTooltips.createTask,
              disabled:
                selectedData.length !== 1 ||
                this.isAReplySelected(selectedData) ||
                this.isUploadDisabled(selectedData[0].receiver_type, selectedData[0].receiver),
              hide: !canCreateTask || this.props.hasEmrIntegrationCredential,
              func: () => this.createTask(),
            },

            {
              text: 'Edit',
              icon: <EditOutlined />,
              disabled: canEdit || selectedData.length !== 1,
              func: this.editPatientOpen,
            },
            {
              text: 'Update Assigned Document',
              icon: <EditOutlined />,
              disabled: canEdit || selectedData.length !== 1,
              func: this.updateDocumentOpen,
            },
            {
              text: selectedData.length > 1 ? 'Add Labels' : 'Add/Edit Label',
              icon: <TagOutlined />,
              disabled:
                selectedData.length === 0 ||
                this.isAReplySelected(selectedData),
              func: this.editTag,
            },
            {
              type: 'dropdown',
              text: this.state.markCompleteButtonOption,
              icon: <CheckCircleOutlined />,
              disabled:
                selectedData.length !== 1 ||
                this.isAReplySelected(selectedData),
              menu: markCompletedMenu,
            },
          ]}
        />
      </div>
    );
  }
}

const passedProps = {
  getSelectedPageData: getAssignedRecordsByPage,
  getPageDataWithQuery: getAssignedByQuery,
  data: 'assigned_data',
  mode: 'Assigned',
  enableSelectAll: true,
  columnTitles: assignedColumns,
  markRecordAsReceived: true,
};
AssignedRecords.defaultProps = {
  selectedData: [],
  downloadUrl: '',
  downloadFilename: '',
  autoUploadEmr: '',
};

AssignedRecords.propTypes = {
  selectedData: PropTypes.array,
  openModal: PropTypes.func.isRequired,
  printUserRecord: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  downloadUrl: PropTypes.string,
  downloadFilename: PropTypes.string,
  downloadZip: PropTypes.func.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  onOpenEhrDrawer: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  search: PropTypes.object.isRequired,
  autoUploadEmr: PropTypes.string,
  currentOrganizationId: PropTypes.string.isRequired,
  markDocumentAsCompleted: PropTypes.func.isRequired,
  departmentUsers: PropTypes.array.isRequired,
  assignBackAndComplete: PropTypes.func.isRequired,
  referringProvider: PropTypes.string,
  referringProviderOffice: PropTypes.string,
  hasEmrIntegrationCredential: PropTypes.bool.isRequired,
};

export default RecordsHOC(passedProps)(
  connect(
    (state) => ({
      selectedData: state.inboxTable.recordData,
      downloadUrl: state.records.downloadUrl,
      downloadFilename: state.records.downloadFilename,
      data: state.records.assigned_data,
      errorMessage: state.records.errorMessage,
      autoUploadEmr: state.auth.data.autoUploadEmr,
      documentCategories: state.auth.data.documentCategories,
      currentOrganizationId: state.auth.currentOrganizationId,
      allDocumentStatuses: state.userProfile.allDocumentStatuses,
      departmentUsers: state.records.department_users,
      referringProvider: state.records.referringProvider,
      referringProviderOffice: state.records.referringProviderOffice,
      hasEmrIntegrationCredential: state.auth.hasEmrIntegrationCredential,
    }),
    {
      printUserRecord,
      downloadZip,
      openModal,
      triggerAutoUpload,
      markDocumentAsCompleted,
      assignBackAndComplete,
    }
  )(AssignedRecords)
);
